import { faDiamond } from "@jewlr/sharp-solid-svg-icons/faDiamond"
import {
  Box,
  Button,
  Flex,
  FontAwesomeIcon,
  Typography,
} from "@jewlr/storybook/core"
import PropTypes from "prop-types"
import { Link } from "react-router-dom"
import styled from "styled-components"

import ResponsiveVideo from "areas/landing/elements/landing-responsive-video"
import Image from "components/landing/image"
import { openModal } from "components/modal"
import UIButton from "components/ui/button"
import UIImage from "components/ui/image"
import { isTestMode } from "helpers/application"
import { cn } from "styles/utils"

const FlexContainer = styled(Flex).attrs((props) => ({
  flexDirection: "column",
  height: "100%",
  justifyContent: "flex-end",
  left: 0,
  pb: { _: "20px", desktop: "40px", widescreen: "60px" },
  position: "absolute",
  pt: { _: "20px", desktop: "40px", widescreen: "60px" },
  px: { _: "20px", desktop: "40px", widescreen: "60px" },
  top: 0,
  width: "100%",
  ...props,
}))``

const StyledButton = styled(Button).attrs((props) => ({
  bg: "primary",
  color: "white",
  forwardedAs: Link,
  height: "44px",
  px: 2,
  width: "fit-content",
  ...props,
}))``

const BannerLink = styled(Link)`
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 2;
`

const LandingBannerItem = ({
  element,
  firstElement,
  homepage,
  item,
  lazyload,
}) => {
  const { url: block_url } = element.data
  const {
    image,
    style,
    text_container_style,
    texts,
    url: item_url,
    video,
  } = item

  const handleClick = () => {
    if (!isTestMode) {
      dataLayer.push({
        event: "clicked-homepage-banner",
      })
    }
  }

  return (
    <Box position="relative" width="100%" {...style}>
      {item_url && !block_url && (
        <BannerLink
          aria-hidden
          data-lc="hero-banner-item"
          to={item_url}
          {...(homepage && { onClick: handleClick })}
        />
      )}
      {image && <Image image={image} lazyload={lazyload} />}
      {video && (
        <ResponsiveVideo id={element.id} lazyload={lazyload} video={video} />
      )}
      {texts?.length > 0 && (
        <FlexContainer {...text_container_style}>
          {texts.map((text, i) => {
            if (text.type === "cta") {
              const url = item_url && !text.url ? item_url : text.url
              return (
                <StyledButton
                  data-lc="hero-banner-item"
                  key={`${element.id}-banner-item-${i}`}
                  tabIndex="-1"
                  to={url}
                  {...text.style}
                >
                  <Typography
                    dangerouslySetInnerHTML={{
                      __html: text.text,
                    }}
                    fontWeight="600"
                  />
                </StyledButton>
              )
            } else if (text.type === "confetti") {
              return (
                <Flex
                  alignItems="center"
                  fontSize={{ _: "30px", tablet: "42px" }}
                  fontWeight="700"
                  key={`${element.id}-banner-item-${i}`}
                  lineHeight={{ _: "1", tablet: "1.2" }}
                  textTransform="uppercase"
                  {...text.style}
                >
                  <FontAwesomeIcon
                    fontSize={{ _: "14px", tablet: "24px" }}
                    icon={faDiamond}
                    mr="4px"
                  />
                  <Typography
                    dangerouslySetInnerHTML={{
                      __html: text.text,
                    }}
                  />
                </Flex>
              )
            } else if (text.type === "image") {
              return (
                <Image
                  containerStyle={text.style}
                  firstElement={firstElement}
                  image={text.image}
                  key={`${element.id}-banner-item-${i}`}
                  lazyload={lazyload}
                />
              )
            } else if (text.type === "code-ref") {
              if (text.text === "BESTSALE") {
                return sl.config.sale_key.includes("cyber-monday") ? (
                  <div
                    className="xlarge:flex-row xlarge:gap-24 flex w-full flex-col items-center justify-center gap-10"
                    key={`${element.id}-banner-item-${i}`}
                  >
                    <UIImage
                      alt="+ Extra 20%*"
                      className="large:w-3/4 desktop:w-3/5 tablet:w-9/10 max-w-256 w-3/5"
                      height="43"
                      lazyload={false}
                      src={require("images/layout/extra-20-off.svg")}
                      width="256"
                    />
                    <div className="text-black-print desktop:text-18 tablet:text-28 text-18 mobile:text-20 large:px-32 large:py-16 border-black-print border-2 border-dashed px-24 py-12 font-bold">
                      USE CODE: <span className="text-primary">BESTSALE</span>
                    </div>
                  </div>
                ) : (
                  <div
                    className="desktop:flex-row tablet:gap-24 flex flex-col items-center justify-center gap-10"
                    key={`${element.id}-banner-item-${i}`}
                  >
                    <UIImage
                      alt="+ Extra 20%*"
                      className="desktop:hidden w-3/5"
                      height="58"
                      lazyload={false}
                      src={require("images/layout/extra-20-off-mobile.svg")}
                      width="347"
                    />
                    <UIImage
                      alt="+ Extra 20% Off*"
                      className="desktop:block w-288 large:w-auto hidden"
                      height="58"
                      lazyload={false}
                      src={require("images/layout/extra-20-off-desktop.svg")}
                      width="450"
                    />
                    <div className="tablet:text-28 text-18 large:px-32 large:py-16 border-2 border-dashed border-white px-24 py-12 font-bold">
                      USE CODE: <span className="text-spotlight">BESTSALE</span>
                    </div>
                  </div>
                )
              } else if (text.text === "terms") {
                return (
                  <UIButton
                    className={cn(
                      "text-16 font-medium",
                      sl.config.sale_key.includes("cyber-monday")
                        ? "text-black-print"
                        : "text-white"
                    )}
                    key={`${element.id}-banner-item-${i}`}
                    onClick={() => openModal("#best-sale-terms")}
                    text
                    underline
                  >
                    *Terms & Conditions Apply.
                  </UIButton>
                )
              }
            }

            const variant =
              text.type === "title"
                ? "display-md"
                : text.type === "subtitle"
                  ? "h1"
                  : "body-lg"

            return (
              <Typography
                dangerouslySetInnerHTML={{
                  __html: text.text,
                }}
                key={`${element.id}-banner-item-${i}`}
                variant={variant}
                {...text.style}
              />
            )
          })}
        </FlexContainer>
      )}
    </Box>
  )
}

LandingBannerItem.propTypes = {
  element: PropTypes.object,
  firstElement: PropTypes.bool,
  homepage: PropTypes.bool,
  item: PropTypes.object,
  lazyload: PropTypes.bool,
}

export default LandingBannerItem
